import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { AlertTriangle, Settings } from 'lucide-react'
import { AdminUpdateDepositStatusDtoStatus } from '@/schema'
import { useMutateDepositTransaction } from '../../../api/hooks/useMutateDepositTransaction'
import { Icons } from '@/common/ui/icons'

interface UpdateDepositStatusModalProps {
	id: string
	currentStatus: string
}

const statusLabels: Record<AdminUpdateDepositStatusDtoStatus, string> = {
	[AdminUpdateDepositStatusDtoStatus.CREATED]: 'สร้างแล้ว',
	[AdminUpdateDepositStatusDtoStatus.PENDING]: 'รอดำเนินการ',
	[AdminUpdateDepositStatusDtoStatus.PROCESSING]: 'กำลังตรวจสอบ',
	[AdminUpdateDepositStatusDtoStatus.COMPLETED]: 'สำเร็จ',
	[AdminUpdateDepositStatusDtoStatus.MANUAL_COMPLETED]:
		'รอ Payment Gateway ตรวจสอบ',
	[AdminUpdateDepositStatusDtoStatus.PAYMENT_GATEWAY_FAILED]:
		'Payment Gateway ล้มเหลว',
	[AdminUpdateDepositStatusDtoStatus.FAILED]: 'ไม่สำเร็จ',
	[AdminUpdateDepositStatusDtoStatus.CANCELED]: 'ยกเลิก',
	[AdminUpdateDepositStatusDtoStatus.EXPIRED]: 'หมดเวลา',
}

const warningStatuses = [
	AdminUpdateDepositStatusDtoStatus.COMPLETED,
	AdminUpdateDepositStatusDtoStatus.FAILED,
	AdminUpdateDepositStatusDtoStatus.CANCELED,
]

export default function UpdateDepositStatusModal({
	id,
	currentStatus,
}: UpdateDepositStatusModalProps) {
	const [open, setOpen] = useState(false)
	const [selectedStatus, setSelectedStatus] = useState<string>('')
	const [note, setNote] = useState('')
	const { updateStatus } = useMutateDepositTransaction()

	const handleUpdateStatus = () => {
		if (!selectedStatus) return

		updateStatus.mutate(
			{ id, status: selectedStatus, note: note || undefined },
			{
				onSuccess: () => {
					setOpen(false)
					setSelectedStatus('')
					setNote('')
				},
			},
		)
	}

	const showWarning =
		selectedStatus &&
		warningStatuses.includes(
			selectedStatus as AdminUpdateDepositStatusDtoStatus,
		)

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className="border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100"
				>
					<Settings className="mr-2 h-4 w-4" />
					อัพเดทสถานะ
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>อัพเดทสถานะการฝากเงิน</DialogTitle>
					<DialogDescription>
						เปลี่ยนสถานะธุรกรรมการฝากเงินเป็นสถานะอื่น
					</DialogDescription>
				</DialogHeader>

				{/* Critical Warning */}
				<div className="rounded-lg border-2 border-red-500 bg-red-50 p-4 dark:bg-red-950">
					<div className="flex items-start gap-3">
						<AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
						<div className="space-y-2 text-sm">
							<p className="font-bold text-red-900 dark:text-red-100">
								⚠️ คำเตือนสำคัญ
							</p>
							<p className="text-red-800 dark:text-red-200">
								การอัพเดทสถานะนี้
								<strong className="underline">ไม่ได้ทำธุรกรรมจริง</strong>
							</p>
							<p className="text-red-800 dark:text-red-200">
								คุณต้อง<strong>ตรวจสอบและดำเนินการด้วยตัวเอง</strong>
								ก่อนเปลี่ยนสถานะ เช่น ตรวจสอบว่าเงินเข้าจริง
								หรือทำรายการฝากเงินให้ผู้เล่นแล้ว
							</p>
						</div>
					</div>
				</div>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="current-status" className="text-sm font-medium">
							สถานะปัจจุบัน
						</Label>
						<div className="rounded-md border p-3 text-sm">
							{statusLabels[
								currentStatus as AdminUpdateDepositStatusDtoStatus
							] || currentStatus}
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="new-status" className="text-sm font-medium">
							สถานะใหม่ <span className="text-red-500">*</span>
						</Label>
						<Select value={selectedStatus} onValueChange={setSelectedStatus}>
							<SelectTrigger id="new-status">
								<SelectValue placeholder="เลือกสถานะ" />
							</SelectTrigger>
							<SelectContent>
								{Object.values(AdminUpdateDepositStatusDtoStatus).map(
									(status) => (
										<SelectItem key={status} value={status}>
											{statusLabels[status]}
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
					</div>

					{showWarning && (
						<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
							<div className="flex items-start gap-2">
								<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
								<div className="space-y-1 text-sm">
									<p className="font-medium text-amber-800">คำเตือน</p>
									{selectedStatus ===
										AdminUpdateDepositStatusDtoStatus.COMPLETED && (
										<p className="text-amber-700">
											การเปลี่ยนเป็นสถานะ &ldquo;สำเร็จ&rdquo;
											จะเพิ่มเครดิตให้กับผู้เล่น
											<br />
											<strong>กรุณาตรวจสอบให้แน่ใจว่าเงินเข้าระบบแล้ว</strong>
										</p>
									)}
									{selectedStatus ===
										AdminUpdateDepositStatusDtoStatus.FAILED && (
										<p className="text-amber-700">
											การเปลี่ยนเป็นสถานะ &ldquo;ไม่สำเร็จ&rdquo;
											จะยกเลิกรายการฝากนี้
											<br />
											<strong>กรุณาตรวจสอบว่าเงินไม่ได้เข้าระบบ</strong>
										</p>
									)}
									{selectedStatus ===
										AdminUpdateDepositStatusDtoStatus.CANCELED && (
										<p className="text-amber-700">
											การเปลี่ยนเป็นสถานะ &ldquo;ยกเลิก&rdquo;
											จะยกเลิกรายการฝากนี้
											<br />
											<strong>การดำเนินการนี้ไม่สามารถย้อนกลับได้</strong>
										</p>
									)}
								</div>
							</div>
						</div>
					)}

					<div className="space-y-2">
						<Label htmlFor="note" className="text-sm font-medium">
							หมายเหตุ (ถ้ามี)
						</Label>
						<Textarea
							id="note"
							placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
							value={note}
							onChange={(e) => setNote(e.target.value)}
							className="min-h-[80px]"
						/>
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => {
							setOpen(false)
							setSelectedStatus('')
							setNote('')
						}}
						disabled={updateStatus.isPending}
					>
						ยกเลิก
					</Button>
					<Button
						onClick={handleUpdateStatus}
						disabled={!selectedStatus || updateStatus.isPending}
						className="bg-purple-600 hover:bg-purple-700"
					>
						{updateStatus.isPending ? (
							<>
								<Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
								กำลังดำเนินการ...
							</>
						) : (
							'ยืนยันการเปลี่ยนแปลง'
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
